import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Positions Table Component
 * Shows Open positions, Pending orders, and Closed positions
 * Supports closing individual positions and close all
 */
import { useState, useEffect, useRef } from 'react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper function to format date in Turkey timezone
const formatTurkeyTime = (dateStr, locale) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString(locale, {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const PositionsTable = () => {
  const tx = useTraderText();
  const { isDark } = useTraderTheme();
  const { t } = useTraderLanguage();
  const { prices } = useWebSocket();
  const [activeTab, setActiveTab] = useState('open');
  const [positions, setPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [closedPositions, setClosedPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closingPosition, setClosingPosition] = useState(null);
  const [showConfirmClose, setShowConfirmClose] = useState(null);
  const [showConfirmCloseAll, setShowConfirmCloseAll] = useState(false);

  // Fetch positions from API
  const positionRequest = useRef(0);
  const fetchPositions = async () => {
    const request = ++positionRequest.current;
    try {
      const token = localStorage.getItem('trader_token');
      if (!token) return;

      const response = await fetch(`${API_BASE}/api/trader/positions?status=open`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if(request===positionRequest.current && localStorage.getItem('trader_token')===token)setPositions(data);
      }
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  };

  // Fetch closed positions
  const fetchClosedPositions = async () => {
    try {
      const token = localStorage.getItem('trader_token');
      if (!token) return;

      const response = await fetch(`${API_BASE}/api/trader/positions?status=closed`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setClosedPositions(data);
      }
    } catch (error) {
      console.error('Failed to fetch closed positions:', error);
    }
  };

  // Fetch pending orders
  const fetchPendingOrders = async () => {
    try {
      const token = localStorage.getItem('trader_token');
      if (!token) return;

      const response = await fetch(`${API_BASE}/api/trader/orders?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingOrders(data);
      }
    } catch (error) {
      console.error('Failed to fetch pending orders:', error);
    }
  };

  useEffect(() => {
    fetchPositions();
    fetchPendingOrders();
    fetchClosedPositions();
    
    // Refresh every 5 seconds
    const interval = setInterval(() => {
      fetchPositions();
      fetchPendingOrders();
      fetchClosedPositions();
    }, 5000);
    const refresh=()=>{fetchPositions();fetchPendingOrders();fetchClosedPositions();};
    window.addEventListener('trader-data-changed',refresh);
    return () => {clearInterval(interval);window.removeEventListener('trader-data-changed',refresh);};
  }, []);

  // Server valuation includes tenant spreads, contract size, swap and fees.
  const livePositions = positions;
  const totalProfit = livePositions.reduce((sum, pos) => sum + (pos.profit_loss || 0), 0);

  // Close a single position
  const handleClosePosition = async (position) => {
    setClosingPosition(position.id);
    try {
      const token = localStorage.getItem('trader_token');
      const response = await fetch(`${API_BASE}/api/trader/positions/${position.id}/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        toast.success(tx(`Position ${position.symbol} closed successfully`));
        window.dispatchEvent(new Event('trader-data-changed'));
        fetchPositions();
        fetchClosedPositions();
      } else {
        const error = await response.json();
        toast.error(tx(error.detail || 'Failed to close position'));
      }
    } catch (error) {
      console.error('Error closing position:', error);
      toast.error(tx('Failed to close position'));
    } finally {
      setClosingPosition(null);
      setShowConfirmClose(null);
    }
  };

  // Close all positions
  const handleCloseAllPositions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('trader_token');
      const response = await fetch(`${API_BASE}/api/trader/positions/close-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(tx(`Closed ${result.closed_count} positions`));
        if(result.errors?.length)toast.error(tx(`${result.errors.length} positions could not be closed.`));
        window.dispatchEvent(new Event('trader-data-changed'));
        fetchPositions();
        fetchClosedPositions();
      } else {
        const error = await response.json();
        toast.error(tx(error.detail || 'Failed to close all positions'));
      }
    } catch (error) {
      console.error('Error closing all positions:', error);
      toast.error(tx('Failed to close all positions'));
    } finally {
      setLoading(false);
      setShowConfirmCloseAll(false);
    }
  };

  const tabs = [
    { id: 'open', label: t('trading.openPositions'), count: livePositions.length },
    { id: 'pending', label: t('trading.pendingOrders'), count: pendingOrders.length },
    { id: 'closed', label: t('trading.closedPositions'), count: closedPositions.length },
  ];

  const formatPrice = (price, symbol) => {
    if (!price) return '-';
    if (symbol?.includes('JPY') || symbol?.includes('TRY')) return price.toFixed(2);
    if (price > 100) return price.toFixed(2);
    return price.toFixed(5);
  };

  const formatProfit = (profit) => {
    if (profit === undefined || profit === null) return '-';
    const sign = profit >= 0 ? '+' : '';
    return `${sign}${profit.toFixed(2)}`;
  };

  return (
    <div data-testid="positions-panel" className={`tr-positions-panel flex flex-col border-t ${
      isDark ? 'bg-[#0f1724] border-gray-800' : 'bg-white border-gray-200'
    }`}>
      {/* Tabs and Close All button */}
      <div data-testid="positions-toolbar" className={`tr-positions-toolbar flex items-center justify-between px-4 border-b ${
        isDark ? 'border-gray-800' : 'border-gray-200'
      }`}>
        <div className="tr-positions-tabs flex" role="tablist" aria-label={tx('Positions')}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-400'
                  : isDark
                    ? 'border-transparent text-gray-400 hover:text-white'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
              data-testid={`positions-tab-${tab.id}`}
              role="tab"
              aria-selected={activeTab === tab.id}
            >
              {tx(tab.label)}
              {tab.count > 0 && (
                <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                  isDark ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {activeTab === 'open' && livePositions.length > 0 && (
          <button
            onClick={() => setShowConfirmCloseAll(true)}
            disabled={loading}
            className="px-4 py-1.5 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded transition-colors"
            data-testid="close-all-positions"
          >
            {tx(loading ? 'Closing...' : t('trading.closeAllPositions'))}
          </button>
        )}
      </div>

      {/* Confirm Close All Modal */}
      {showConfirmCloseAll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`p-6 rounded-xl shadow-xl max-w-sm mx-4 ${isDark ? 'bg-[#1a2332]' : 'bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-500" size={24} />
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Close All Positions? ")}</h3>
            </div>
            <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("This will close ")}{livePositions.length}{tx(" open position(s) at current market prices. Total P&L: ")}<span className={totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                {formatProfit(totalProfit)}
              </span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmCloseAll(false)}
                className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
              >{tx("Cancel ")}</button>
              <button
                onClick={handleCloseAllPositions}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white"
              >
                {tx(loading ? 'Closing...' : 'Close All')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div data-testid="positions-table-scroll" className="tr-positions-scroll flex-1 overflow-auto" style={{ maxHeight: '200px' }}>
        {tx(activeTab === 'open' && (
          <table data-testid="open-positions-table" className="w-full text-sm">
            <thead className={`sticky top-0 ${isDark ? 'bg-[#1a2332]' : 'bg-gray-100'}`}>
              <tr>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.instrument')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.type')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.amount')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.openTime')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.openPrice')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Current ")}</th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.stopLoss')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.takeProfit')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.profit')}
                </th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {livePositions.length === 0 ? (
                <tr>
                  <td colSpan={10} className={`px-4 py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("No open positions ")}</td>
                </tr>
              ) : (
                livePositions.map((pos) => (
                <tr 
                  key={pos.id} 
                  className={`border-b ${isDark ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}
                >
                  <td className={`px-4 py-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {pos.symbol}
                  </td>
                  <td className={`px-4 py-2 ${pos.direction === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                    {tx(pos.direction?.toUpperCase() || 'BUY')}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {(pos.volume || 0).toFixed(2)}
                  </td>
                  <td className={`px-4 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatTurkeyTime(pos.opened_at, tx('en-US'))}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatPrice(pos.entry_price, pos.symbol)}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatPrice(pos.current_price, pos.symbol)}
                  </td>
                  <td className={`px-4 py-2 text-right text-red-400`}>
                    {pos.stop_loss ? formatPrice(pos.stop_loss, pos.symbol) : '-'}
                  </td>
                  <td className={`px-4 py-2 text-right text-green-400`}>
                    {pos.take_profit ? formatPrice(pos.take_profit, pos.symbol) : '-'}
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${
                    (pos.profit_loss || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formatProfit(pos.profit_loss)}
                  </td>
                  <td className="px-4 py-2">
                    {showConfirmClose === pos.id ? (
                      <div className="flex gap-1">
                        <button 
                          onClick={() => handleClosePosition(pos)}
                          data-testid={`confirm-close-position-${pos.id}`}
                          disabled={closingPosition === pos.id}
                          className="p-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                        >
                          {tx(closingPosition === pos.id ? '...' : 'Yes')}
                        </button>
                        <button 
                          onClick={() => setShowConfirmClose(null)}
                          data-testid={`cancel-close-position-${pos.id}`}
                          className="p-1 rounded bg-gray-500 hover:bg-gray-600 text-white text-xs"
                        >{tx("No ")}</button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setShowConfirmClose(pos.id)}
                        className="p-1 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                        title={tx("Close position")}
                        data-testid={`close-position-${pos.id}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        ))}

        {tx(activeTab === 'pending' && (
          <table data-testid="pending-orders-table" className="w-full text-sm">
            <thead className={`sticky top-0 ${isDark ? 'bg-[#1a2332]' : 'bg-gray-100'}`}>
              <tr>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.instrument')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.type')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.amount')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Price ")}</th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.stopLoss')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.takeProfit')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Created ")}</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {pendingOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className={`px-4 py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("No pending orders ")}</td>
                </tr>
              ) : (
                pendingOrders.map((order) => (
                <tr 
                  key={order.id} 
                  className={`border-b ${isDark ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}
                >
                  <td className={`px-4 py-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {order.symbol}
                  </td>
                  <td className={`px-4 py-2 ${order.side === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                    {tx(order.type?.toUpperCase())} {tx(order.side?.toUpperCase())}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {(order.volume || 0).toFixed(2)}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatPrice(order.price, order.symbol)}
                  </td>
                  <td className={`px-4 py-2 text-right text-red-400`}>
                    {order.stop_loss ? formatPrice(order.stop_loss, order.symbol) : '-'}
                  </td>
                  <td className={`px-4 py-2 text-right text-green-400`}>
                    {order.take_profit ? formatPrice(order.take_profit, order.symbol) : '-'}
                  </td>
                  <td className={`px-4 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatTurkeyTime(order.created_at, tx('en-US'))}
                  </td>
                  <td className="px-4 py-2">
                    <button 
                      className="p-1 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                      title={tx("Cancel order")}
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        ))}

        {tx(activeTab === 'closed' && (
          <table data-testid="closed-positions-table" className="w-full text-sm">
            <thead className={`sticky top-0 ${isDark ? 'bg-[#1a2332]' : 'bg-gray-100'}`}>
              <tr>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.instrument')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.type')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.amount')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.openTime')}
                </th>
                <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Close Time ")}</th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.openPrice')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.closePrice')}
                </th>
                <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('trading.profit')}
                </th>
              </tr>
            </thead>
            <tbody>
              {closedPositions.length === 0 ? (
                <tr>
                  <td colSpan={8} className={`px-4 py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("No closed positions ")}</td>
                </tr>
              ) : (
                closedPositions.map((pos) => (
                <tr 
                  key={pos.id} 
                  className={`border-b ${isDark ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}
                >
                  <td className={`px-4 py-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {pos.symbol}
                  </td>
                  <td className={`px-4 py-2 ${pos.direction === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                    {tx(pos.direction?.toUpperCase() || 'BUY')}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {(pos.volume || 0).toFixed(2)}
                  </td>
                  <td className={`px-4 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatTurkeyTime(pos.opened_at, tx('en-US'))}
                  </td>
                  <td className={`px-4 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatTurkeyTime(pos.closed_at, tx('en-US'))}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatPrice(pos.entry_price, pos.symbol)}
                  </td>
                  <td className={`px-4 py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatPrice(pos.close_price, pos.symbol)}
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${
                    (pos.profit_loss || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formatProfit(pos.profit_loss)}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        ))}
      </div>

      {/* Total P/L for open positions */}
      {activeTab === 'open' && livePositions.length > 0 && (
        <div data-testid="positions-total-profit" className={`tr-positions-total px-4 py-2 flex justify-end border-t ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Total P/L:")}{' '}
            <span className={`font-medium ${totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatProfit(totalProfit)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

export default PositionsTable;
